import type { ReactNode } from 'react'

import { clsx } from 'clsx'
import { createPortal } from 'react-dom'

import type { SelectOption } from '../searchable-select'

// ============================================================================
// Checkbox indicator
// ============================================================================

export function CheckboxIndicator({ isSelected }: { isSelected: boolean }): JSX.Element {
  return (
    <div
      className={clsx(
        'flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center',
        isSelected ? 'bg-primary border-primary' : 'border-border'
      )}
    >
      {isSelected && (
        <svg className="w-3 h-3 text-white dark:text-primary-foreground" fill="currentColor" viewBox="0 0 12 12">
          <path d="M10 3L4.5 8.5L2 6" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        </svg>
      )}
    </div>
  )
}

// ============================================================================
// Option item
// ============================================================================

interface DropdownOptionItemProps<T> {
  isHighlighted: boolean
  isSelected: boolean
  option: SelectOption<T>
  onToggle: (option: SelectOption<T>) => void
}

export function DropdownOptionItem<T>({ isHighlighted, isSelected, option, onToggle }: DropdownOptionItemProps<T>): JSX.Element {
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(option) }
  }
  return (
    <div
      aria-selected={isSelected}
      className={clsx(
        'px-3 py-2 cursor-pointer flex items-center gap-3 transition-colors duration-150',
        isHighlighted && 'bg-accent',
        isSelected && !isHighlighted && 'bg-muted',
        option.disabled && 'opacity-50 cursor-not-allowed'
      )}
      role="option"
      tabIndex={0}
      onClick={() => onToggle(option)}
      onKeyDown={handleKeyDown}
    >
      <CheckboxIndicator isSelected={isSelected} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground truncate">{option.label}</div>
        {option.description && (
          <div className="text-xs text-muted-foreground truncate">{option.description}</div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Loading / empty states
// ============================================================================

export function DropdownLoading(): JSX.Element {
  return (
    <div className="p-8 text-center text-muted-foreground">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
    </div>
  )
}

export function DropdownEmpty({ noOptionsText }: { noOptionsText: string }): JSX.Element {
  return <div className="p-8 text-center text-muted-foreground text-sm">{noOptionsText}</div>
}

// ============================================================================
// Custom option wrapper
// ============================================================================

export function CustomOptionWrapper<T>({
  isHighlighted, isSelected, option, renderOption, onToggle,
}: {
  isHighlighted: boolean
  isSelected: boolean
  option: SelectOption<T>
  renderOption: (opt: SelectOption<T>, sel: boolean, hi: boolean) => ReactNode
  onToggle: (opt: SelectOption<T>) => void
}): JSX.Element {
  return (
    <div
      aria-selected={isSelected}
      className="cursor-pointer"
      role="option"
      tabIndex={0}
      onClick={() => onToggle(option)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(option) } }}
    >
      {renderOption(option, isSelected, isHighlighted)}
    </div>
  )
}

// ============================================================================
// Options list
// ============================================================================

interface DropdownOptionsProps<T> {
  filteredOptions: SelectOption<T>[]
  highlightedIndex: number
  isLoading: boolean
  noOptionsText: string
  renderOption?: (option: SelectOption<T>, isSelected: boolean, isHighlighted: boolean) => ReactNode
  value: T[]
  onToggle: (option: SelectOption<T>) => void
}

export function DropdownOptions<T>({
  filteredOptions, highlightedIndex, isLoading, noOptionsText, renderOption, value, onToggle,
}: DropdownOptionsProps<T>): JSX.Element {
  if (isLoading) { return <DropdownLoading /> }
  if (filteredOptions.length === 0) { return <DropdownEmpty noOptionsText={noOptionsText} /> }
  return (
    <>
      {filteredOptions.map((option, index) => {
        const isSelected = value.includes(option.value)
        const isHighlighted = index === highlightedIndex
        if (renderOption) {
          return (
            <CustomOptionWrapper
              key={String(option.value)}
              isHighlighted={isHighlighted}
              isSelected={isSelected}
              option={option}
              renderOption={renderOption}
              onToggle={onToggle}
            />
          )
        }
        return (
          <DropdownOptionItem
            key={String(option.value)}
            isHighlighted={isHighlighted}
            isSelected={isSelected}
            option={option}
            onToggle={onToggle}
          />
        )
      })}
    </>
  )
}

// ============================================================================
// Search bar
// ============================================================================

interface DropdownSearchProps {
  onSearchChange: (q: string) => void
  searchInputRef: React.RefObject<HTMLInputElement>
  searchPlaceholder: string
  searchQuery: string
}

export function DropdownSearch({ onSearchChange, searchInputRef, searchPlaceholder, searchQuery }: DropdownSearchProps): JSX.Element {
  return (
    <div className="p-2 border-b border-border">
      <input
        ref={searchInputRef}
        className={clsx(
          'w-full px-3 py-2 rounded-md border border-border bg-card text-foreground',
          'placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring'
        )}
        placeholder={searchPlaceholder}
        type="text"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
      />
    </div>
  )
}

// ============================================================================
// Dropdown panel
// ============================================================================

interface DropdownPanelProps<T> {
  dropdownPosition: { top: number; left: number; width: number } | null
  filteredOptions: SelectOption<T>[]
  highlightedIndex: number
  isLoading: boolean
  isModal: boolean
  isMobile: boolean
  listRef: React.RefObject<HTMLDivElement>
  maxSelections?: number
  maxVisibleItems: number
  noOptionsText: string
  onSearchChange: (q: string) => void
  onToggle: (option: SelectOption<T>) => void
  renderOption?: (option: SelectOption<T>, isSelected: boolean, isHighlighted: boolean) => ReactNode
  searchInputRef: React.RefObject<HTMLInputElement>
  searchPlaceholder: string
  searchQuery: string
  value: T[]
}

export function DropdownPanel<T>({
  dropdownPosition, filteredOptions, highlightedIndex, isLoading, isModal, isMobile,
  listRef, maxSelections, maxVisibleItems, noOptionsText, onSearchChange, onToggle,
  renderOption, searchInputRef, searchPlaceholder, searchQuery, value,
}: DropdownPanelProps<T>): JSX.Element {
  const positionStyle =
    !isMobile && !isModal && dropdownPosition
      ? { top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px`, width: `${dropdownPosition.width}px` }
      : undefined

  return (
    <div
      className={clsx(
        'bg-card border border-border rounded-md shadow-lg overflow-hidden',
        isMobile || isModal ? 'w-full max-w-md mx-auto' : 'absolute z-50'
      )}
      style={positionStyle}
    >
      <DropdownSearch
        searchInputRef={searchInputRef}
        searchPlaceholder={searchPlaceholder}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
      />
      <div
        ref={listRef}
        aria-multiselectable="true"
        className="overflow-y-auto"
        role="listbox"
        style={{ maxHeight: `${maxVisibleItems * 38}px` }}
      >
        <DropdownOptions
          filteredOptions={filteredOptions}
          highlightedIndex={highlightedIndex}
          isLoading={isLoading}
          noOptionsText={noOptionsText}
          renderOption={renderOption}
          value={value}
          onToggle={onToggle}
        />
      </div>
      {maxSelections && (
        <div className="p-2 border-t border-border text-xs text-muted-foreground text-center">
          {value.length} / {maxSelections} selected
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Portal wrapper
// ============================================================================

interface DropdownPortalProps {
  children: React.ReactNode
  dropdownRef: React.RefObject<HTMLDivElement>
  isModal: boolean
  isMobile: boolean
  onClose: () => void
}

export function DropdownPortal({ children, dropdownRef, isModal, isMobile, onClose }: DropdownPortalProps): JSX.Element {
  const handleBackdropKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') { onClose() }
  }

  if (isMobile || isModal) {
    return createPortal(
      <>
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          role="presentation"
          onClick={onClose}
          onKeyDown={handleBackdropKeyDown}
        />
        <div
          ref={dropdownRef}
          className={clsx(
            'fixed z-50',
            isMobile ? 'bottom-0 left-0 right-0 rounded-t-xl' : 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'
          )}
        >
          {children}
        </div>
      </>,
      document.body
    )
  }

  return createPortal(<div ref={dropdownRef}>{children}</div>, document.body)
}
